/**
 * Junction — connects pipe faces at network nodes.
 *
 * Junction state (P, h) is updated by the LIVE solver loop in
 * nsNetwork.networkSimpleStep Step 3 using MASS-FLOW-WEIGHTED averages of the
 * adjacent pipe boundary cells (the CLAUDE.md-canonical convention), with an
 * arithmetic-mean fallback at zero flow.
 *
 * N8 NOTE: a second, DEAD update path (`updateJunction`) previously lived
 * here with a CONFLICTING min(P_connected) pressure convention. It had zero
 * callers (grep-verified: only re-exported from index.ts) and was deleted
 * rather than aligned — one junction-pressure convention, one code path.
 */

import type { Junction, JunctionConnection, PipeSegment } from '../types';

/**
 * Create a junction connecting pipe faces.
 */
export function createJunction(
  id: string,
  type: 'component' | 'merge' | 'split' | 'boundary',
  connections: JunctionConnection[],
  P_init: number = 101325,
  h_init: number = 104930,
  componentId?: string,
): Junction {
  return { id, type, connections, P: P_init, h: h_init, componentId };
}

/**
 * Apply junction state as boundary condition to connected pipe faces.
 *
 * For outflow faces: set the face enthalpy to junction enthalpy
 * For all faces: match the pressure at the boundary cell
 */
export function applyJunctionToFaces(
  junction: Junction,
  pipes: Map<string, PipeSegment>,
): void {
  for (const conn of junction.connections) {
    const pipe = pipes.get(conn.pipeId);
    if (!pipe) continue;

    const cellIdx = conn.faceIndex === 0 ? 0 : pipe.nCells - 1;

    // Set boundary cell pressure to junction pressure
    pipe.cells[cellIdx].P = junction.P;

    // For outflow faces, set upstream enthalpy to junction enthalpy
    const face = pipe.faces[conn.faceIndex];
    const isOutflow = (conn.direction === 'outflow' && face.mdot > 0) ||
                      (conn.direction === 'inflow' && face.mdot < 0);
    if (isOutflow) {
      pipe.cells[cellIdx].h = junction.h;
    }
  }
}

/**
 * Check mass conservation at a junction.
 * Returns the mass imbalance [kg/s] — should be ~0.
 */
export function junctionMassImbalance(
  junction: Junction,
  pipes: Map<string, PipeSegment>,
): number {
  let netMdot = 0;

  for (const conn of junction.connections) {
    const pipe = pipes.get(conn.pipeId);
    if (!pipe) continue;

    const face = pipe.faces[conn.faceIndex];
    // Positive mdot = flow in the pipe's forward direction
    // inflow to junction = positive contribution
    if (conn.direction === 'inflow') {
      netMdot += face.mdot;
    } else {
      netMdot -= face.mdot;
    }
  }

  return netMdot;
}
